import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import './ConsentFormsList.css';

const ConsentFormsList = () => {
    const [consents, setConsents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, active, revoked
    const [showConsentModal, setShowConsentModal] = useState(false);
    const [selectedConsent, setSelectedConsent] = useState(null);

    useEffect(() => {
        loadAllConsents();
    }, []);

    const loadAllConsents = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/consents/all');
            setConsents(response.data.consents || []);
        } catch (error) {
            console.error('Error loading consents:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleViewConsent = async (consent) => {
        try {
            const response = await api.get(`/consent/download/${consent.id}`);
            setSelectedConsent(response.data.consent);
            setShowConsentModal(true);
        } catch (error) {
            console.error('Error viewing consent:', error);
            alert('Failed to load consent details');
        }
    };

    const handleDownloadPDF = async (consent) => {
        try {
            const response = await api.get(`/admin/consents/${consent.id}/pdf`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `consent-${consent.patientName}-${new Date(consent.signedAt).toLocaleDateString()}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert('Failed to download consent PDF');
        }
    };

    // Filter consents based on search and status
    const filteredConsents = consents.filter(consent => {
        const matchesSearch = consent.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            consent.patientEmail.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === 'all' ||
            (filterStatus === 'active' && consent.isActive) ||
            (filterStatus === 'revoked' && !consent.isActive);
        return matchesSearch && matchesStatus;
    });

    const activeCount = consents.filter(c => c.isActive).length;
    const revokedCount = consents.filter(c => !c.isActive).length;

    if (loading) {
        return (
            <div className="consents-list-loading">
                <div className="loading-spinner"></div>
                <p>Loading consent forms...</p>
            </div>
        );
    }

    return (
        <div className="consents-list-container">
            <div className="consents-list-header">
                <div className="header-title">
                    <h1>📄 Patient Consent Forms</h1>
                    <p className="header-subtitle">Manage and view all patient consent forms</p>
                </div>
                <div className="header-stats">
                    <div className="stat-card">
                        <span className="stat-number">{consents.length}</span>
                        <span className="stat-label">Total</span>
                    </div>
                    <div className="stat-card active">
                        <span className="stat-number">{activeCount}</span>
                        <span className="stat-label">Active</span>
                    </div>
                    <div className="stat-card revoked">
                        <span className="stat-number">{revokedCount}</span>
                        <span className="stat-label">Revoked</span>
                    </div>
                </div>
            </div>

            <div className="consents-list-controls">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="Search by patient name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
                <div className="filter-buttons">
                    <button
                        className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('all')}
                    >
                        All
                    </button>
                    <button
                        className={`filter-btn ${filterStatus === 'active' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('active')}
                    >
                        Active
                    </button>
                    <button
                        className={`filter-btn ${filterStatus === 'revoked' ? 'active' : ''}`}
                        onClick={() => setFilterStatus('revoked')}
                    >
                        Revoked
                    </button>
                </div>
            </div>

            {filteredConsents.length === 0 ? (
                <div className="no-consents-found">
                    <p>No consent forms found</p>
                </div>
            ) : (
                <div className="consents-table-container">
                    <table className="consents-table">
                        <thead>
                            <tr>
                                <th>Patient Name</th>
                                <th>Email</th>
                                <th>Signed Date</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredConsents.map((consent) => (
                                <tr key={consent.id}>
                                    <td>
                                        <div className="patient-cell">
                                            <div className="patient-avatar">
                                                {consent.patientName.split(' ').map(n => n[0]).join('').toUpperCase()}
                                            </div>
                                            <span className="patient-name">{consent.patientName}</span>
                                        </div>
                                    </td>
                                    <td className="email-cell">{consent.patientEmail}</td>
                                    <td className="date-cell">{formatDate(consent.signedAt)}</td>
                                    <td>
                                        <span className={`status-badge ${consent.isActive ? 'active' : 'revoked'}`}>
                                            {consent.isActive ? '✓ Active' : '✗ Revoked'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="btn-action view"
                                                onClick={() => handleViewConsent(consent)}
                                                title="View Full Consent"
                                            >
                                                👁️ View
                                            </button>
                                            <button
                                                className="btn-action download"
                                                onClick={() => handleDownloadPDF(consent)}
                                                title="Download PDF"
                                            >
                                                ⬇️ PDF
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Consent Modal */}
            {showConsentModal && selectedConsent && (
                <div className="consent-modal-overlay" onClick={() => setShowConsentModal(false)}>
                    <div className="consent-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="consent-modal-header">
                            <h2>Consent Form Details</h2>
                            <button className="modal-close-btn" onClick={() => setShowConsentModal(false)}>✕</button>
                        </div>
                        <div className="consent-modal-content">
                            <div className="consent-modal-section">
                                <div className="consent-modal-status">
                                    <span className={`consent-badge ${selectedConsent.isActive ? 'active' : 'revoked'}`}>
                                        {selectedConsent.isActive ? '✓ Active' : '✗ Revoked'}
                                    </span>
                                </div>
                            </div>

                            <div className="consent-modal-section">
                                <h3>Signer Information</h3>
                                <div className="modal-info-grid">
                                    <div className="modal-info-item">
                                        <span className="modal-label">Full Name:</span>
                                        <span className="modal-value">{selectedConsent.fullName}</span>
                                    </div>
                                    <div className="modal-info-item">
                                        <span className="modal-label">Email:</span>
                                        <span className="modal-value">{selectedConsent.userEmail}</span>
                                    </div>
                                    <div className="modal-info-item">
                                        <span className="modal-label">Signed On:</span>
                                        <span className="modal-value">{formatDate(selectedConsent.signedAt)}</span>
                                    </div>
                                    {selectedConsent.revokedAt && (
                                        <>
                                            <div className="modal-info-item">
                                                <span className="modal-label">Revoked On:</span>
                                                <span className="modal-value">{formatDate(selectedConsent.revokedAt)}</span>
                                            </div>
                                            {selectedConsent.revokedReason && (
                                                <div className="modal-info-item full-width">
                                                    <span className="modal-label">Revocation Reason:</span>
                                                    <span className="modal-value">{selectedConsent.revokedReason}</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="consent-modal-section">
                                <h3>Consent Text</h3>
                                <div className="consent-text-container">
                                    {selectedConsent.consentText}
                                </div>
                            </div>

                            {selectedConsent.signature && (
                                <div className="consent-modal-section">
                                    <h3>Signature</h3>
                                    <div className="modal-signature-container">
                                        <img src={selectedConsent.signature} alt="Signature" className="modal-signature-image" />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="consent-modal-footer">
                            <button className="btn-modal-close" onClick={() => setShowConsentModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsentFormsList;
